
import React, { useEffect, useRef, useState } from 'react';
import Footer from './Footer';
import Checkmark from '../../../assets/checkmark.js';
import { getMarkDown, useDetectMobile, getButtonWithArrow, useNextButtonWidthClass, getInlineMarkDown } from '../utils';
import '../../../stylesheets/triagebot/MultiSelect.scss';
import { formatCardLoadTiming } from '../utils';
import { useTiming } from '../TimingContext';
import { useCardLoadTiming } from '../hooks/useCardLoadTiming';


//************************************* */

const MultiSelectMessage = ({ message, sendMessage, disabled }) => {

  const nextButton = message?.buttons?.[0]?.displayText
  ? message?.buttons?.[0]?.displayText
  : '';

  const [choices, setChoices] = useState([...getChoices()]);
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const headingRef = useRef(null);

  const showCheckMark = choices.length && (choices[choices.length - 1].value || '').toLowerCase() !== 'none';
  const hasNoneOption = choices.length ? choices[choices.length - 1].value.toLowerCase() === 'none' : false;
  const hasSelection = choices.filter(choice => choice.selected).length;

  const questionId = (message && message.data && message.data.length && message.data[0] && message.data[0].question_id)
    ? message.data[0].question_id
    : '';

    const instructions = (message?.subheadertext)
    ? message.subheadertext
    : '';

  const isDisabled = submitted || disabled;

  const { isMobile } = useDetectMobile();
  const nextButtonClass = useNextButtonWidthClass();

  const subHeaderText = (message?.data?.[0]?.subHeaderText)
    ? message.data[0].subHeaderText
    : '';
  const hasSubHeader = Boolean(subHeaderText);
  const subHeaderId = hasSubHeader ? `${questionId || 'multi-select'}-subheader` : undefined;

  function getChoices() {
    // Are choices located at data[1]?
    if (message && (message.data.length > 1) && message.data[1].length && message.data[1][0] && message.data[1][0].displayText)
      return message.data[1];

    // Are the choices located at data[0]?
    if (message && message.data.length && message.data[0].item) {
      return message.data[0].forEach((item) => {
        message.choices.push({
          displayText: item,
          title: item,
          value: item,
        });
      });
    }

    return [];
  }

  const unselectNone = () => {
    setChoices(
      [...choices.map(choice => {
        if (choice.value.toLowerCase() === 'none')
          choice.selected = false;
        return choice;
      })]
    )
  };

  const choicePress = (choice) => {
    if (submitted) return;

    const noneSelected = choice.value.toLowerCase() === 'none';

    choice.selected = noneSelected
      ? true
      : !choice.selected;

    choices.forEach(choice => {
      if (noneSelected) {
        if (choice.value.toLowerCase() !== 'none')
          choice.selected = false;
      }
      else {
        if (choice.value.toLowerCase() === 'none')
          choice.selected = false;
      }
    });

    setChoices([...choices]);

    if (noneSelected)
      submitPress();

  };

  const submitPress = (event) => {
    if (submitted) {
      unselectNone();
      sendMessage('rewind', questionId, questionId, cardName);
      setSubmitted(false);
      setEditing(true);
      return;
    }

    const isMedicalHistory = message?.data?.length && (message.data?.[0]?.current_step === 'medical_history');

    const selectedValues = choices?.filter(choice => choice.selected)?.map(choice => {
        if (isMedicalHistory) {
          return `${choice.value}:${choice.displayText}`;
        }
        return choice.value;
      });

    const submitValue = selectedValues?.join(',');

    setSubmitted(true);

    editing
      ? sendMessage('message', `##edit@${message.data[0].question_id}@${submitValue || 'none'}`, `##edit@${message.data[0].question_id}@${submitValue || 'none'}`, cardName)
      : sendMessage('message', submitValue || 'none', submitValue || 'none', cardName);
  };

  const { logTiming } = useTiming();
  const cardName = (message.data && message.data[0] && message.data[0].question_id) ? message.data[0].question_id : "MultiSelect";

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      headingRef.current?.focus({ preventScroll: true });
    }, 500);

    return () => clearTimeout(focusTimer);
  }, [cardName]);

  useCardLoadTiming({
              sentAt: message._sentAt,
              cardName,
              logTiming,
              formatCardLoadTiming
          });
  

  return (
    <div className={isDisabled ? 'disabled multi-select card' : 'multi-select card'}>
      <div className='content' >
        <div className='header' id={questionId}>
          <h2 className='headerText headerTextColor' ref={headingRef} tabIndex={-1}> {getInlineMarkDown(message)} </h2>
          <div className='instructions'> 
            <p>  {instructions} </p> 
          </div> 
          {
            hasSubHeader &&
            <div id={subHeaderId} className='subheader'>
              {getMarkDown(subHeaderText)}
            </div>
          }
        </div>
        <div className='body' role='group' aria-label='Answer options' aria-describedby={subHeaderId}>
          {choices.map((choice, key) => {
            const choiceCheckmarkId = `${questionId || 'multi-select'}-${key}`;
            return (
              <button
                id='button-multi-select'
                key={key}
                className={(isMobile ? 'mobile ' : 'desktop ') + (choice.selected ? 'multi-button selected' : 'multi-button')}
                onClick={() => choicePress(choice)}
                onMouseDown={(e) => e.preventDefault()}
                disabled={submitted || disabled}
                role='checkbox'
                aria-checked={choice.selected ? 'true' : 'false'}
                aria-label={choice.displayText}
              >
                {
                  (choice.selected) &&
                  <Checkmark
                    className='checkmark'
                    role="img"
                    ariaLabelledby={`${choiceCheckmarkId}-selected`}
                    id={choiceCheckmarkId}
                  />
                }
                <span>{choice.displayText}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Footer
        showChangeButton={questionId && submitted && !message.isLastMessage}
        onAccepted={submitPress}
        onDenied={() => sendMessage('fastforward')}
        disabled={disabled}
        divID={questionId}
        message={message}
      >
        <button
          onClick={submitPress}
          className={`submit-button ${nextButtonClass} ${(hasNoneOption && !hasSelection) ? 'next-disabled' : ''}`.trim()}
          disabled={isDisabled || (hasNoneOption && !hasSelection)}
          style={{ visibility: submitted ? 'hidden' : 'visible', 'display': submitted ? 'none' : '' }}
          aria-hidden={submitted ? 'true' : 'false'}
        >
          {getButtonWithArrow(nextButton)} 
          </button>
      </Footer>

    </div>
  );
};

export default MultiSelectMessage;
